import { motion } from "framer-motion";
import { ArrowRight, DraftingCompass } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

export const Professionnels = () => {
  return (
    <section
      id="professionnels"
      data-testid="professionnels-section"
      className="relative bg-charcoal-deep text-white py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      {/* subtle ambient light */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] bg-bronze/8 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* LEFT — copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-3 mb-8 bg-white/10 border border-white/20 backdrop-blur-sm pl-4 pr-5 py-2.5">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-white text-xs sm:text-sm uppercase tracking-[0.25em] font-medium">
                Vous êtes professionnel ?
              </span>
            </div>

            <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-normal">
              Pour les professionnels
              <br />
              du <span className="italic text-bronze-light">bâtiment</span> et de <span className="italic text-bronze-light">l'immobilier.</span>
            </h2>

            <div className="mt-10 space-y-6 text-white/75 text-base lg:text-lg leading-relaxed font-light max-w-2xl">
              <p>
                DEM BAT accompagne les <span className="text-white">architectes, régies, promoteurs et entreprises générales</span>.
                Des petits chantiers aux projets d'envergure à Genève et en Suisse romande.
              </p>
              <p>
                Plâtrerie, peinture, isolation, décoration haut de gamme, revêtements de sol minéraux.
                Des finitions qui tiennent la promesse des plans.
              </p>
              <p>
                Un interlocuteur unique, des délais respectés, un résultat à la hauteur de vos exigences.
              </p>
            </div>

            <div className="mt-12">
              <a
                href="#devis"
                data-testid="professionnels-cta"
                className="group inline-flex items-center justify-center gap-3 bg-white hover:bg-bronze hover:text-white text-charcoal-deep px-8 py-5 text-xs uppercase tracking-[0.25em] transition-all duration-300"
              >
                Discutons de votre projet
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </a>
            </div>
          </motion.div>

          {/* RIGHT — sober architectural visual */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.9, ease, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative border border-white/15 p-10 lg:p-12 bg-white/[0.02]">
              {/* drafting icon */}
              <DraftingCompass
                className="w-12 h-12 text-bronze mb-10"
                strokeWidth={1}
              />

              {/* Targeted audience list */}
              <ul className="space-y-5">
                {[
                  { label: "Architectes", note: "Cahiers des charges respectés à la lettre" },
                  { label: "Régies", note: "Suivi de plusieurs lots simultanément" },
                  { label: "Promoteurs", note: "Du gros œuvre aux finitions livrées" },
                  { label: "Entreprises générales", note: "Sous-traitance fiable et planifiée" },
                ].map((item, i) => (
                  <li
                    key={item.label}
                    data-testid={`professionnels-target-${i}`}
                    className="flex items-baseline gap-4 border-b border-white/10 pb-5 last:border-0 last:pb-0"
                  >
                    <span className="font-serif text-bronze text-sm tracking-widest">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-serif text-white text-lg lg:text-xl">{item.label}</div>
                      <div className="text-white/55 text-xs uppercase tracking-widest mt-1">
                        {item.note}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
